// 色を自由に選ぶ画面（段階 C の2 の 7g-3、2026-10-26。前は jQuery の excolor プラグイン。http://excolor.ramiro.mx/ ）。
// 隠しの input（#chaInpPickColor）の横に色の箱を出し、input を押すと色相のバー・彩度と明るさの四角・R G B と 16 進の欄・OK を出す。
// 見た目は前と同じ DOM・id・inline の style と画像（common/img/excolor/）、CSS（css/excolor.css）。動きもプラグインのまま移した:
// 初めに Math.random で色の箱の id（mc＋数字）を作る（同じ回数だけ乱数を使う）。ページのどこで Enter・Esc を押しても既定の動きを止める
// （Enter は OK、Esc は閉じる）。値が "r,g,b" の形なら OK でもその形で書く。画面の外を押すと閉じる。OK で input の値を "#rrggbb" にして onOk。
// 前にあった console.log と、外に漏れていた変数（root_path など）は移していない
import * as jq from './jq.js';

const HEX: Record<string, 1> = {};
'0123456789ABCDEFabcdef'.split('').forEach((c) => (HEX[c] = 1));
const DIGIT: Record<string, number> = { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 46: 46 };
const SYSKEY: Record<number, string> = { 0: 'system keys', 8: 'backspace', 118: 'ctrl v', 86: 'ctrl v', 99: 'ctrl c', 67: 'ctrl c' };

// 色の変換（プラグインと同じ式）
function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const k = h / 360, A = s / 100, w = v / 100;
  if (A == 0) return { r: w * 255, g: w * 255, b: w * 255 };
  const l = k * 6, m = Math.floor(l), i = w * (1 - A), z = w * (1 - A * (l - m)), y = w * (1 - A * (1 - (l - m)));
  let j: number, p: number, b: number;
  if (m == 0) (j = w), (p = y), (b = i);
  else if (m == 1) (j = z), (p = w), (b = i);
  else if (m == 2) (j = i), (p = w), (b = y);
  else if (m == 3) (j = i), (p = z), (b = w);
  else if (m == 4) (j = y), (p = i), (b = w);
  else (j = w), (p = i), (b = z);
  return { r: Math.round(j * 255), g: Math.round(p * 255), b: Math.round(b * 255) };
}
function hexToRgb(n: string): { r: number; g: number; b: number } {
  const k = '0123456789ABCDEF';
  n = n.toUpperCase();
  const at = (i: number): number => (16 * k.indexOf(n.charAt(i)) + k.indexOf(n.charAt(i + 1))) * 1;
  return { r: at(0), g: at(2), b: at(4) };
}
function rgbToHsv(p: number, n: number, m: number): { h: number; s: number; v: number } {
  const l = Math.min(Math.min(p, n), m), i = Math.max(Math.max(p, n), m), q = i - l;
  const t = i != 0 ? (255 * q) / i : 0;
  let j = 0;
  if (t != 0) {
    if (p == i) j = (n - m) / q;
    else if (n == i) j = 2 + (m - p) / q;
    else if (m == i) j = 4 + (p - n) / q;
  } else j = -1;
  j = j * 60;
  if (j < 0) j = j + 360;
  return { h: Math.min(360, Math.round(j)), s: Math.min(100, Math.round((t * 100) / 255)), v: Math.min(100, Math.round((i * 100) / 255)) };
}
function toHex(r: unknown, g: unknown, b: unknown): string {
  const d = '0123456789abcdef';
  let o = '';
  for (const x of [r, g, b]) {
    const dec = parseInt(String(x));
    o += d.charAt(parseInt(String(dec / 16))) + d.charAt(dec % 16);
  }
  return o;
}

/** input に色を自由に選ぶ画面を付ける（プラグインの $(input).excolor({ root_path, callback_on_ok })） */
export function colorPicker(sel: string, rootPath: string, onOk: () => void): void {
  for (const b of jq.els(sel) as HTMLInputElement[]) attach(b, rootPath, onOk);
}

function attach(b: HTMLInputElement, root: string, onOk: () => void): void {
  let hue = 0, sat = 1, val = 1; // ad・aD・d（色相のバーの位置 0〜119・彩度・明るさ）
  let inside = false, insideTimer = 0; // az（カーソルが中にある）
  let open = false, ok = false; // O・ap
  let rgbOut = false; // aI（値が "r,g,b" の形だったら、OK でも "r,g,b" で書く）
  let hexTxt = '';
  let dragPicker: HTMLElement | null = null, dragSlider: HTMLElement | null = null;
  let grabX = 0, grabY = 0, pasteTimer = 0;
  let S: HTMLElement | null = null, sw: HTMLElement | null = null, slider: HTMLElement | null = null, picker: HTMLElement | null = null;
  let hueEl: HTMLElement | null = null, grad: HTMLElement | null = null, sample: HTMLElement | null = null;
  let inR: HTMLInputElement | null = null, inG: HTMLInputElement | null = null, inB: HTMLInputElement | null = null, inHex: HTMLInputElement | null = null, okBtn: HTMLElement | null = null;
  let hueOff = { top: 0, left: 0 }, sbOff = { top: 0, left: 0 };
  const hover = (on: boolean, ms: number): void => {
    clearTimeout(insideTimer);
    insideTimer = window.setTimeout(() => {
      inside = on;
    }, ms);
  };
  const hoverable = (el: Element, ms: number): void => {
    el.addEventListener('mouseenter', () => hover(true, ms));
    el.addEventListener('mouseleave', () => hover(false, ms));
  };
  // 画像を先に読んでおく（プラグインと同じ）
  for (const f of ['sel.gif', 'slider.gif', 'bg.png', 'hue.png', 'ok.png', 'shbg.png', 'transp0.gif', 'transp.gif']) new Image().src = root + f;
  hoverable(b, 100);
  // 色の箱（input を span で包み、横に箱の input）
  const h0 = jq.outerHeight(b);
  const wrap = document.createElement('span');
  wrap.setAttribute('style', 'margin:0;padding:0 ' + h0 + 'px 0 0;display:inline-block;border:none;background:none;line-height:auto;');
  b.parentNode!.insertBefore(wrap, b);
  wrap.appendChild(b);
  const rnd = (' ' + Math.random()).split('.');
  const id = 'mc' + rnd[1];
  jq.append(wrap, '&nbsp;<input class="excolor_clrbox" readonly="readonly" id="' + id + '" type="text" style="display:none;cursor:pointer;width:' + (h0 - 4) + 'px;height:' + (h0 - 4) + 'px;background:none;-khtml-border-radius:2px;-moz-border-radius:2px;-webkit-border-radius:2px;border-radius:2px;border:1px solid black;" />');
  const box = wrap.querySelector('#' + id) as HTMLInputElement;
  hoverable(box, 50);
  box.addEventListener('focus', () => b.focus());
  setTimeout(() => jq.show(box), 150);

  /** input の値を読み直す（ai） */
  const readInput = (): void => {
    const t = (b.value + '').trim();
    hexTxt = '';
    if (t == '') {
      jq.setVal(box, '');
      jq.setCss(box, 'background', 'url(' + root + 'transp.gif) repeat');
      if (S) for (const i of Array.prototype.slice.call(S.querySelectorAll('input')) as HTMLInputElement[]) i.value = '';
      return;
    }
    if (/\d+\,\d+\,\d+/i.test(t)) {
      rgbOut = true;
      const a = t.split(',');
      hexTxt = toHex(a[0], a[1], a[2]);
    } else {
      for (let j = 0; j < t.length; j++) if (t.charAt(j) != '#' && t.charAt(j) in HEX && hexTxt.length < 6) hexTxt += t.charAt(j);
      hexTxt = ('000000' + hexTxt).slice(-Math.max(6, hexTxt.length)).slice(-6);
    }
    const c = hexToRgb(hexTxt), hsv = rgbToHsv(c.r, c.g, c.b);
    hue = Math.min(119, Math.max(0, 120 - Math.round((hsv.h * 1) / 3)));
    sat = hsv.s;
    val = hsv.v;
    jq.setCss(box, 'background', '#' + hexTxt);
  };
  readInput();

  // 画面の中の更新（aB・V・v）
  const placeCursors = (): void => {
    if (slider) {
      jq.setCss(slider, 'left', hueOff.left + 'px');
      jq.setCss(slider, 'top', hueOff.top + hue + 'px');
    }
    if (picker && grad) {
      jq.setCss(picker, 'left', sbOff.left + Math.round(sat * 1.3) - 4 + 'px');
      jq.setCss(picker, 'top', sbOff.top + jq.height(grad) - 4 - Math.round(val * 1.3) + 'px');
    }
  };
  const hueHex = (s: number, v: number): string => {
    const c = hsvToRgb(-1 * (hue - 119) * 3, s, v);
    return toHex(c.r, c.g, c.b);
  };
  const paint = (): void => {
    jq.setCss(grad, 'background-color', '#' + hueHex(100, 100));
    jq.setCss(sample, 'background-color', '#' + hueHex(sat, val));
    jq.setCss(sample, 'background-image', 'none');
    jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) 0 0 no-repeat');
  };
  const writeInputs = (): void => {
    const c = hsvToRgb(-1 * (hue - 119) * 3, sat, val);
    jq.setVal(inR, Math.round(c.r) * 1);
    jq.setVal(inG, Math.round(c.g) * 1);
    jq.setVal(inB, Math.round(c.b) * 1);
    jq.setVal(inHex, toHex(c.r, c.g, c.b));
  };
  const fromRgbInputs = (): void => {
    const c = rgbToHsv(+inR!.value * 1, +inG!.value * 1, +inB!.value * 1);
    hue = Math.min(119, Math.max(0, -1 * (Math.round((c.h * 1) / 3) - 120)));
    sat = c.s;
    val = c.v;
    placeCursors();
    paint();
    jq.setVal(inHex, toHex(+inR!.value * 1, +inG!.value * 1, +inB!.value * 1));
  };
  const fromHexInput = (): void => {
    let m = (inHex!.value || '').trim();
    if (m.length > 0 && m.length < 6) m = ('000000' + m).slice(-6);
    for (let l = 0; l < m.length; l++) if (!(m.charAt(l) in HEX)) return;
    if (m == '') {
      hue = 119;
      sat = 0;
      val = 0;
    } else {
      const c = hexToRgb(m);
      jq.setVal(inR, c.r);
      jq.setVal(inG, c.g);
      jq.setVal(inB, c.b);
      const h = rgbToHsv(c.r, c.g, c.b);
      hue = Math.min(119, Math.max(0, -1 * (Math.round((h.h * 1) / 3) - 120)));
      sat = h.s;
      val = h.v;
    }
    placeCursors();
    paint();
    if (m == '') {
      jq.setCss(sample, 'background-image', 'url(' + root + 'transp.gif)');
      jq.setCss(sample, 'background-repeat', 'repeat');
      jq.setVal(box, '');
      jq.setCss(box, 'background', 'url(' + root + 'transp.gif) repeat');
      jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) -20px 0 no-repeat');
      for (const i of Array.prototype.slice.call(S!.querySelectorAll('input')) as HTMLInputElement[]) i.value = '';
    }
  };
  /** 閉じる（ab。OK のあとなら値を input に書いて onOk） */
  const close = (): void => {
    if (!open) return;
    for (const el of [slider, picker, sw]) if (el && el.parentNode) el.parentNode.removeChild(el);
    inside = true;
    if (ok) {
      const hex = '#' + toHex(+inR!.value * 1, +inG!.value * 1, +inB!.value * 1);
      if (inR!.value.trim() == '' && inG!.value.trim() == '' && inB!.value.trim() == '') {
        jq.setCss(box, 'background', 'url(' + root + 'transp.gif) repeat');
        b.value = '';
      } else {
        jq.setCss(box, 'background', hex);
        b.value = rgbOut ? +inR!.value * 1 + ',' + +inG!.value * 1 + ',' + +inB!.value * 1 : hex;
      }
      ok = false;
      onOk();
    }
    if (S && S.parentNode) S.parentNode.removeChild(S);
    open = false;
  };
  const rgbClamp = (el: HTMLInputElement): void => {
    if (el.value == '') el.value = '0';
    if (isNaN(+el.value * 1)) el.value = '0';
    if (+el.value * 1 > 255) el.value = '255';
    if (+el.value * 1 < 0) el.value = '0';
  };
  const pt = (ev: MouseEvent | TouchEvent): [number, number] => {
    const m = ev as MouseEvent;
    if (m.pageX || m.pageY) return [m.pageX, m.pageY];
    const t = (ev as TouchEvent).targetTouches[0];
    return [t.pageX, t.pageY];
  };

  /** 開く（W） */
  const build = (): void => {
    readInput();
    jq.append(
      document.body,
      '<div id="excolor_colorpicker"><div id="excolor_colorpicker_wrapper"><div id="excolor_grad_wrap"><div id="excolor_grad"><div style="width:130px;height:130px;float:left;background-image:url(' +
        root +
        'bg.png);background-position:0 0;background-repeat:no-repeat;border:1px solid white;padding:0;margin:0;"></div></div></div><div id="excolor_hue_wrap" style="width:20px;height:130px;float:left;padding:15px 8px 0px 10px;margin:0;border:none;"><div id="excolor_hue" style="width:20px;height:130px;float:left;background:url(' +
        root +
        'hue.png) 0px 0 no-repeat;margin:0;padding:0;border:none;"></div></div><div id="excolor_data" style="float:left;font-size:10px;font-family: Verdana, Arial, Helvetica, Sans-serif;width:66px;height:140px;margin:0;padding:0;border:none;"><div id="excolor_sample_wrapper"><div id="excolor_sample"></div></div><div class="excolor_dataitem first"><b class="label">R</b><input id="excolor_r" class="excolor_input excolor_rgb" type="text" size="3" maxlength="3" /></div><div class="excolor_dataitem" style="float:left;padding:3px 0 0 0;margin:0;border:none;"><b class="label">G</b><input id="excolor_g" class="excolor_input excolor_rgb" type="text" size="3" maxlength="3" /></div><div class="excolor_dataitem"><b class="label">B</b><input id="excolor_b" class="excolor_input excolor_rgb" type="text" size="3" maxlength="3" /></div><div class="excolor_dataitem last"><b class="label">#</b><input id="excolor_hex" class="excolor_input" type="text" size="6" maxlength="6" /></div><div style="width:66px;height:15px;padding:0;margin:0;border:none;float:left;"><div id="excolor_ok">OK</div><div id="excolor_close">X</div></div></div></div></div><div id="excolor_picker"></div><div id="excolor_slider"></div><div id="excolor_switcher"></div>',
    );
    const off = jq.offset(b)!;
    S = document.querySelector('body > div#excolor_colorpicker') as HTMLElement;
    jq.setCss(S, 'left', off.left + 'px');
    jq.setCss(S, 'top', off.top + jq.outerHeight(b) + 'px');
    hoverable(S, 50);
    inHex = S.querySelector('input#excolor_hex');
    sw = document.querySelector('body > div#excolor_switcher');
    slider = document.querySelector('body > div#excolor_slider');
    picker = document.querySelector('body > div#excolor_picker');
    jq.show(S);
    jq.show('body > div#excolor_slider, body > div#excolor_picker');
    hueEl = S.querySelector('div#excolor_hue');
    grad = S.querySelector('div#excolor_grad div');
    sample = S.querySelector('#excolor_sample');
    inR = S.querySelector('input#excolor_r');
    inG = S.querySelector('input#excolor_g');
    inB = S.querySelector('input#excolor_b');
    okBtn = S.querySelector('#excolor_ok');
    const closeBtn = S.querySelector('#excolor_close') as HTMLElement;
    sbOff = jq.offset(grad)!;
    hueOff = jq.offset(hueEl)!;
    // 透明の切りかえ
    sw!.addEventListener('click', () => {
      if (inR!.value.trim() == '' && inG!.value.trim() == '' && inB!.value.trim() == '') {
        jq.setCss(sample, 'background-image', 'none');
        jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) 0 0 no-repeat');
        writeInputs();
      } else {
        jq.setCss(sample, 'background-image', 'url(' + root + 'transp.gif)');
        jq.setCss(sample, 'background-repeat', 'repeat');
        for (const i of Array.prototype.slice.call(S!.querySelectorAll('input')) as HTMLInputElement[]) i.value = '';
        jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) -20px 0 no-repeat');
      }
    });
    jq.setCss(sw, 'left', hueOff.left - 1 + 'px');
    jq.setCss(sw, 'top', hueOff.top - 15 + 'px');
    hoverable(sw!, 50);
    hoverable(slider!, 50);
    hoverable(picker!, 50);
    picker!.addEventListener('dblclick', () => okBtn!.click());
    const grab = (el: HTMLElement, isPicker: boolean) => (ev: Event): void => {
      const o = jq.offset(el)!;
      const p = pt(ev as MouseEvent);
      grabX = p[0] - o.left;
      grabY = p[1] - o.top;
      if (isPicker) dragPicker = el;
      else dragSlider = el;
      jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) 0 0 no-repeat');
      ev.preventDefault();
    };
    for (const t of ['mousedown', 'touchstart']) {
      picker!.addEventListener(t, grab(picker!, true));
      slider!.addEventListener(t, grab(slider!, false));
      hueEl!.addEventListener(t, (ev) => {
        const p = pt(ev as MouseEvent);
        if (p[1] >= hueOff.top + 5 && p[1] <= hueOff.top + jq.height(hueEl) - 6) {
          hue = Math.min(119, Math.max(0, p[1] - hueOff.top - 5));
          jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) 0 0 no-repeat');
          placeCursors();
          paint();
          writeInputs();
          const so = jq.offset(slider)!;
          grabX = p[0] - so.left;
          grabY = p[1] - so.top;
          dragSlider = slider;
          ev.preventDefault();
        }
      });
      grad!.addEventListener(t, (ev) => {
        const p = pt(ev as MouseEvent);
        sat = Math.min(100, Math.max(1, Math.round((p[0] - sbOff.left - 1) / 1.3)));
        val = Math.min(100, Math.max(1, -1 * (Math.round((p[1] - sbOff.top - 1) / 1.3) - 100)));
        jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) 0 0 no-repeat');
        placeCursors();
        jq.setCss(sample, 'background-color', '#' + hueHex(sat, val));
        jq.setCss(sample, 'background-image', 'none');
        writeInputs();
        dragPicker = picker;
        const po = jq.offset(picker)!;
        grabX = p[0] - po.left;
        grabY = p[1] - po.top;
        ev.preventDefault();
      });
    }
    // 欄に打つ（RGB は数だけ・16 進は 0〜F だけ）
    for (const input of Array.prototype.slice.call(S.querySelectorAll('input.excolor_input')) as HTMLInputElement[]) {
      const isRgb = input.classList.contains('excolor_rgb');
      input.addEventListener('keypress', (ev) => {
        const ch = String.fromCharCode(ev.which);
        if (isRgb ? !((ch as unknown as number) * 1 in DIGIT) && !(ev.which in SYSKEY) : !(ch in HEX) && !(ev.which in SYSKEY)) ev.preventDefault();
      });
      const onChange = (): void => {
        if (isRgb) {
          rgbClamp(input);
          fromRgbInputs();
        } else fromHexInput();
      };
      input.addEventListener('change', onChange);
      input.addEventListener('keyup', onChange);
    }
    inHex!.onpaste = inHex!.oninput = () => {
      clearTimeout(pasteTimer);
      pasteTimer = window.setTimeout(fromHexInput, 100);
    };
    for (const el of [inR!, inG!, inB!]) {
      el.onpaste = el.oninput = function (this: HTMLInputElement) {
        rgbClamp(this);
        clearTimeout(pasteTimer);
        pasteTimer = window.setTimeout(fromRgbInputs, 100);
      } as (ev: Event) => void;
    }
    okBtn!.addEventListener('click', () => {
      ok = true;
      close();
    });
    okBtn!.addEventListener('mouseenter', () => jq.setCss(okBtn, 'background-position', '0 -15px'));
    okBtn!.addEventListener('mouseleave', () => jq.setCss(okBtn, 'background-position', '0 0'));
    closeBtn.addEventListener('click', () => close());
    closeBtn.addEventListener('mouseenter', () => jq.setCss(closeBtn, 'background-position', '-47px -15px'));
    closeBtn.addEventListener('mouseleave', () => jq.setCss(closeBtn, 'background-position', '-47px 0'));
    for (const el of [picker, slider, hueEl, grad]) jq.markClickable(el, ['mousedown', 'touchstart']);
    for (const el of [sw, okBtn, closeBtn]) jq.markClickable(el, ['click']);
    placeCursors();
    jq.setVal(inHex, hexTxt);
    fromHexInput();
    open = true;
    jq.setCss(sw, 'background', 'url(' + root + 'transp0.gif) ' + (hexTxt == '' ? '-20px' : '0') + ' 0 no-repeat');
  };

  // 文書全体: 引っぱる・Enter で OK・Esc で閉じる（どちらも既定の動きを止める）・外を押すと閉じる
  const up = (): void => {
    dragPicker = null;
    dragSlider = null;
  };
  document.addEventListener('mouseup', up);
  document.addEventListener('touchend', up);
  const move = (ev: Event): void => {
    if (!dragPicker && !dragSlider) return;
    const p = pt(ev as MouseEvent);
    if (dragPicker) {
      ev.preventDefault();
      const i = Math.min(sbOff.left + 125, Math.max(sbOff.left - 4, p[0] - grabX));
      const k = Math.min(sbOff.top + 125, Math.max(sbOff.top - 4, p[1] - grabY));
      jq.setCss(dragPicker, 'left', i + 'px');
      jq.setCss(dragPicker, 'top', k + 'px');
      val = -1 * (Math.round((k - sbOff.top + 5) / 1.3) - 100) + 1;
      sat = Math.round((i - sbOff.left + 5) / 1.3);
      if (val == 1) val = 0;
      if (sat == 1) sat = 0;
      jq.setCss(sample, 'background-color', '#' + hueHex(sat, val));
      jq.setCss(sample, 'background-image', 'none');
      writeInputs();
    }
    if (dragSlider) {
      ev.preventDefault();
      hue = Math.min(119, Math.max(0, p[1] - hueOff.top - grabY));
      jq.setCss(dragSlider, 'left', hueOff.left + 'px');
      jq.setCss(dragSlider, 'top', hueOff.top + hue + 'px');
      paint();
      writeInputs();
    }
  };
  document.addEventListener('mousemove', move);
  document.addEventListener('touchmove', move, { passive: false });
  document.addEventListener('keydown', (ev) => {
    if (ev.keyCode == 27) {
      ev.preventDefault();
      close();
    }
    if (ev.keyCode == 13) {
      ev.preventDefault();
      // 一度開いたあとは、閉じていても前の OK を押したことになる（プラグインと同じ。そのあと閉じるときに色が入る）
      if (okBtn) okBtn.click();
    }
  });
  document.addEventListener('click', () => {
    if (!inside) close();
  });
  b.addEventListener('click', () => {
    if (open) return;
    for (const el of Array.prototype.slice.call(document.querySelectorAll('body > div#excolor_slider, body > div#excolor_picker, body > div#excolor_colorpicker')) as Element[]) el.remove();
    setTimeout(build, 50);
  });
  b.addEventListener('keyup', () => {
    if (!open) return;
    readInput();
    writeInputs();
    placeCursors();
    jq.setVal(inHex, hexTxt);
    fromHexInput();
  });
  box.addEventListener('click', () => b.click());
  // 押せるものの印（前は jQuery で付けていた）
  jq.markClickable(b, ['click']);
  jq.markClickable(box, ['click']);
}
